import i18n, { PostProcessorModule } from 'i18next'
import { initReactI18next } from 'react-i18next'
import Backend from 'i18next-http-backend'
import { apiUrl } from './utils'

// Extend i18n with custom properties
declare module 'i18next' {
  interface i18n {
    failedLoading: boolean
  }
}

const capitalizeFirstLetter = (str: string, capitalize?: boolean) => {
  if (capitalize === true) {
    return str.charAt(0).toUpperCase() + str.slice(1)
  } else if (capitalize === false) {
    return str.charAt(0).toLowerCase() + str.slice(1)
  } else {
    return str
  }
}

const capitalize: PostProcessorModule = {
  type: 'postProcessor',
  name: 'capitalize',
  process: (value, _key, options: { capitalize?: boolean }) => {
    return capitalizeFirstLetter(value, options.capitalize)
  },
}

export const LANGUAGE_STORAGE_KEY = 'userLanguage'

// Extend i18n with failedLoading prop, to handle request error
i18n.failedLoading = false
i18n.on('failedLoading', () => (i18n.failedLoading = true))

i18n
  .use(initReactI18next) // passes i18n down to react-i18next
  .use(capitalize) // add option to capitalize first letter
  .use(Backend) // getting translations from Pipit
  .init({
    react: {
      useSuspense: false,
    },
    lng:
      typeof window === 'undefined'
        ? undefined
        : (localStorage.getItem(LANGUAGE_STORAGE_KEY) ?? undefined),
    fallbackLng: `en`,
    load: 'languageOnly',
    saveMissing: true, // Enables `missingKey` event
    backend: {
      loadPath: `${apiUrl()}/rest/v1/translations/get-translations/{{lng}}`,
      addPath: null, // This prevents the default /locales/add/ requests when missing keys
      request: (
        _options: unknown,
        url: string,
        _payload: unknown,
        callback: (error: null, response: { status: number; data: string } | null) => void
      ) => {
        fetch(url)
          .then(async (response) => {
            if (!response.ok) {
              throw new Error(`Failed to load translations: ${response.status}`)
            }
            callback(null, { status: response.status, data: await response.text() })
          })
          .catch(() => {
            callback(null, null)
          })
      },
    },
    postProcess: ['capitalize'],
    ns: ['translations'],
    defaultNS: 'translations',
    interpolation: {
      escapeValue: false,
    },
    partialBundledLanguages: true,
    resources: {},
  })

// Expose i18next instance for Playwright language switching
if (typeof window !== 'undefined' && process.env.NEXT_PUBLIC_ENABLE_TEST_HELPERS) {
  ;(window as any).__i18n = i18n
}

// Log missing translation keys
i18n.on('missingKey', (lngs, namespace, key) => {
  console.warn(
    `[Styra] Missing translation key: "${key}" in namespace "${namespace}" for languages:`,
    lngs
  )
})

export default i18n
